// Apolo IDE — Connections modal (Sprint v11+).
//
// Single source of truth UI for every credential in the user's workspace.
// Consumes /api/connections (which already merges llm_adapters +
// user_settings + user_connectors). Listens for the
// `apolo:openConnections` custom event so any view can trigger it.
//
// Backend has bidirectional sync (services/llmAdapters.syncSettingsKeyToAdapter
// + adapters POST/PATCH calls forward sync), so editing any row here writes
// to ONE place and both stores stay aligned automatically.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Activity,
  CheckCircle2,
  Github,
  Globe,
  KeyRound,
  Link2,
  Loader2,
  Pencil,
  Plug,
  Search,
  ShieldCheck,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import { apiFetch } from '../../lib/api.js';
// ntfy (2026-07-10) — notificações no celular (agentes + sessões).
import NtfySection from './NtfySection.jsx';

// ── Inline brand SVGs (reused from CostStatsCard) — guaranteed render ──
function OpenAIMark({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.911 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.182a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .511 4.91 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.998-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.142-.08 4.778-2.758a.795.795 0 0 0 .393-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.495 4.493zM3.6 18.304a4.471 4.471 0 0 1-.534-3.014l.142.085 4.783 2.758a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.499 4.499 0 0 1-6.14-1.646zM2.34 7.896A4.485 4.485 0 0 1 4.706 5.923v5.677a.766.766 0 0 0 .388.677l5.814 3.354-2.02 1.169a.076.076 0 0 1-.071 0l-4.83-2.787A4.504 4.504 0 0 1 2.341 7.872zm16.597 3.856L13.104 8.364l2.015-1.164a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.104v-5.677a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.41 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.499 4.499 0 0 1 6.68 4.66zM8.307 12.863l-2.02-1.164a.08.08 0 0 1-.038-.057V6.074a4.499 4.499 0 0 1 7.376-3.454l-.142.081L8.704 5.46a.795.795 0 0 0-.393.681zm1.098-2.366l2.602-1.5 2.607 1.5v3l-2.597 1.5-2.607-1.5z" />
    </svg>
  );
}
function AnthropicMark({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
      <path d="M17.304 3.541h-3.672l6.696 16.918H24L17.304 3.541zm-10.608 0L0 20.459h3.744l1.37-3.553h7.005l1.369 3.553h3.744L10.536 3.541H6.696zm-.371 10.223l2.291-5.946 2.291 5.946H6.325z" />
    </svg>
  );
}
function DeepSeekMark({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
      <path d="M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 0 1-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 0 0-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 0 1-.465.137 9.597 9.597 0 0 0-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.857.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 0 0 1.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 0 1 1.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.293.625.515.891.681-.802.09-2.14.11-3.054-.618zm1-6.196c0-.193.156-.348.353-.348.044 0 .084.01.121.025a.344.344 0 0 1 .234.324c0 .193-.156.349-.353.349a.349.349 0 0 1-.354-.35zm3.553 1.83c-.23.094-.46.179-.681.187a1.466 1.466 0 0 1-.961-.302c-.336-.231-.575-.402-.673-.84a1.207 1.207 0 0 1 .017-.69c.084-.236.027-.371-.137-.494a.275.275 0 0 1-.107-.215c-.004-.082.046-.155.103-.21.146-.139.331-.176.516-.204.681-.102 1.252.14 1.736.658.342.367.602.787.872 1.205.067.105.137.207.207.31z" />
    </svg>
  );
}

// Provider key → display config. Order = render priority.
const PROVIDER_CONFIG = {
  openai: { label: 'OpenAI', Logo: OpenAIMark, brandColor: '#74AA9C', docsUrl: 'https://platform.openai.com/api-keys' },
  anthropic: { label: 'Anthropic', Logo: AnthropicMark, brandColor: '#D97757', docsUrl: 'https://console.anthropic.com/settings/keys' },
  deepseek: { label: 'DeepSeek', Logo: DeepSeekMark, brandColor: '#4D6BFE', docsUrl: 'https://platform.deepseek.com/api_keys' },
  google: { label: 'Google AI', Logo: Globe, brandColor: '#4285F4', docsUrl: 'https://aistudio.google.com/app/apikey' },
  xai: { label: 'xAI Grok', Logo: Globe, brandColor: '#000000', docsUrl: 'https://console.x.ai' },
  mistral: { label: 'Mistral', Logo: Globe, brandColor: '#FA520F', docsUrl: 'https://console.mistral.ai/api-keys' },
  groq: { label: 'Groq', Logo: Globe, brandColor: '#F55036', docsUrl: 'https://console.groq.com/keys' },
  qwen: { label: 'Qwen / Alibaba', Logo: Globe, brandColor: '#615CED', docsUrl: 'https://dashscope.aliyun.com' },
  kimi: { label: 'Kimi (Moonshot)', Logo: Globe, brandColor: '#3A3AFF', docsUrl: 'https://platform.moonshot.cn' },
  zai: { label: 'Z.ai / GLM', Logo: Globe, brandColor: '#5B5BD6', docsUrl: 'https://z.ai' },
  minimax: { label: 'MiniMax', Logo: Globe, brandColor: '#FBA919', docsUrl: 'https://platform.minimaxi.chat' },
  yi: { label: 'Yi / 01.ai', Logo: Globe, brandColor: '#1E40AF', docsUrl: 'https://platform.lingyiwanwu.com' },
  together: { label: 'Together AI', Logo: Globe, brandColor: '#0F6FFF', docsUrl: 'https://api.together.xyz' },
  openrouter: { label: 'OpenRouter', Logo: Globe, brandColor: '#A855F7', docsUrl: 'https://openrouter.ai/keys' },
};

const ADMIN_KEY_SUFFIX = '_ADMIN_KEY';
const API_KEY_SUFFIX = '_API_KEY';

function ConnectionRow({ providerKey: _providerKey, label, Logo, brandColor, mask, hasValue, docsUrl, settingKey, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!value.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiFetch(`/api/settings/${settingKey}`, {
      method: 'PUT',
      body: { value: value.trim() },
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || `HTTP ${res.status}`);
      return;
    }
    setValue('');
    setEditing(false);
    onSaved?.();
  }, [value, settingKey, onSaved]);

  const handleDisconnect = useCallback(async () => {
    setSaving(true);
    setError(null);
    const res = await apiFetch(`/api/settings/${settingKey}`, { method: 'DELETE' });
    setSaving(false);
    if (!res.ok && res.status !== 404) {
      setError(res.error || `HTTP ${res.status}`);
      return;
    }
    onSaved?.();
  }, [settingKey, onSaved]);

  return (
    <div className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3">
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: hasValue ? `${brandColor}1f` : 'var(--apolo-surface3)' }}
        >
          <Logo
            className="h-4 w-4"
            style={{ color: hasValue ? brandColor : 'var(--apolo-textTertiary)' }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-medium text-apolo-textPrimary">
            {label}
            {hasValue && (
              <CheckCircle2 size={11} className="text-apolo-success" aria-hidden="true" />
            )}
          </div>
          <div className="text-[11px] text-apolo-textTertiary">
            {hasValue ? (
              <>Conectado · <code className="text-apolo-textSecondary">{mask || 'sk-...'}</code></>
            ) : (
              <span>Não conectado · {settingKey}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {hasValue && (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={saving}
              aria-label={`Desconectar ${label}`}
              className="rounded-md p-1.5 text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-error disabled:opacity-50"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            aria-pressed={editing}
            aria-label={editing ? 'Fechar edição' : `Editar ${label}`}
            className="rounded-md p-1.5 text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          >
            {editing ? <X size={12} aria-hidden="true" /> : <Pencil size={12} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 26 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleSave} className="mt-3 flex flex-col gap-2 border-t border-apolo-borderSubtle pt-3">
              {docsUrl && (
                <p className="text-[11px] text-apolo-textTertiary">
                  Cole sua chave abaixo.{' '}
                  <a href={docsUrl} target="_blank" rel="noreferrer" className="underline">
                    Onde obter →
                  </a>
                </p>
              )}
              <div className="flex gap-1.5">
                <input
                  type="password"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="sk-…"
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 font-mono text-xs text-apolo-textPrimary outline-none transition focus:border-apolo-accent"
                />
                <button
                  type="submit"
                  disabled={saving || !value.trim()}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-white transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: brandColor }}
                >
                  {saving ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : 'Conectar'}
                </button>
              </div>
              {error && (
                <p role="alert" className="text-[11px] text-apolo-error">
                  <AlertCircle size={10} className="mr-1 inline" aria-hidden="true" />
                  {error}
                </p>
              )}
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count, accent }) {
  return (
    <header className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-apolo-textTertiary">
      <Icon size={11} style={{ color: accent }} aria-hidden="true" />
      <span>{title}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="rounded-full bg-apolo-surface3 px-1.5 py-0.5 text-[10px] tabular-nums text-apolo-textSecondary">
          {count}
        </span>
      )}
    </header>
  );
}

export default function ConnectionsModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const dialogRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiFetch('/api/connections');
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'Falha ao carregar conexões');
      return;
    }
    setData(res.data);
  }, []);

  useEffect(() => {
    function onOpen() {
      setOpen(true);
      load();
    }
    window.addEventListener('apolo:openConnections', onOpen);
    return () => window.removeEventListener('apolo:openConnections', onOpen);
  }, [load]);

  useEffect(() => {
    if (open && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
    if (!open && dialogRef.current?.open) dialogRef.current.close();
  }, [open]);

  const handleClose = useCallback(() => setOpen(false), []);

  // Build provider rows: union of {adapter status} + {api-key settings}.
  const providerRows = useMemo(() => {
    if (!data) return [];
    const settings = data.settings || [];
    const adapters = data.adapters || [];

    // For each known provider, find matching setting + adapter
    return Object.entries(PROVIDER_CONFIG).map(([key, cfg]) => {
      // Setting key naming convention is <KEY>_API_KEY (deepseek uses DEEPSEEK_API_KEY etc).
      const settingKey = `${key.toUpperCase()}${API_KEY_SUFFIX}`;
      const setting = settings.find((s) => s.key === settingKey);
      // Adapter name varies (openai-api vs openai, anthropic-api vs anthropic, gemini for google).
      const adapterNameCandidates = {
        openai: 'openai-api',
        anthropic: 'anthropic-api',
        google: 'gemini',
      };
      const adapterName = adapterNameCandidates[key] || key;
      const adapter = adapters.find((a) => a.name === adapterName);
      return {
        providerKey: key,
        ...cfg,
        settingKey,
        hasValue: !!(setting?.has_value || adapter?.has_api_key),
        mask: setting?.mask || adapter?.key_mask || null,
      };
    });
  }, [data]);

  // Filter rows
  const filteredProviders = useMemo(() => {
    if (!filter.trim()) return providerRows;
    const needle = filter.toLowerCase().trim();
    return providerRows.filter((p) =>
      p.label.toLowerCase().includes(needle) ||
      p.providerKey.toLowerCase().includes(needle) ||
      p.settingKey.toLowerCase().includes(needle),
    );
  }, [providerRows, filter]);

  // Admin keys: settings ending in _ADMIN_KEY
  const adminKeys = useMemo(() => {
    if (!data) return [];
    return (data.settings || [])
      .filter((s) => s.category === 'billing-admin')
      .map((s) => ({
        ...s,
        provider: s.provider || s.key.replace(/_ADMIN_KEY$/, '').toLowerCase(),
      }));
  }, [data]);

  // Budget
  const budgetSetting = useMemo(() => {
    if (!data) return null;
    return (data.settings || []).find((s) => s.key === 'monthly_budget_usd');
  }, [data]);

  const connectors = data?.connectors || [];

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      aria-labelledby="apolo-connections-title"
      className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary backdrop:bg-apolo-overlay backdrop:backdrop-blur-sm"
    >
      <div className="flex max-h-[85vh] w-[min(44rem,94vw)] flex-col">
        <header className="flex items-center justify-between border-b border-apolo-borderSubtle px-5 py-4">
          <div>
            <h2 id="apolo-connections-title" className="flex items-center gap-2 text-base font-medium">
              <Plug size={16} className="text-apolo-accent" aria-hidden="true" />
              Conexões
            </h2>
            <p className="mt-0.5 text-[11px] text-apolo-textTertiary">
              Adicione uma chave em qualquer linha — propaga automaticamente pra todo o sistema.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            className="rounded-md p-1.5 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        {data?.summary && (
          <div className="grid grid-cols-4 gap-px border-b border-apolo-borderSubtle bg-apolo-borderSubtle text-center">
            {[
              { label: 'Adapters', value: data.summary.adapters_with_keys, total: data.summary.adapters_total },
              { label: 'API keys', value: data.summary.settings_secrets, total: null },
              { label: 'OAuth', value: data.summary.connectors_total, total: null },
              { label: 'Budget', value: data.budget_usd ? `$${data.budget_usd}` : '—', total: null },
            ].map((stat) => (
              <div key={stat.label} className="bg-apolo-surface1 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">{stat.label}</div>
                <div className="text-sm font-medium text-apolo-textPrimary tabular-nums">
                  {stat.value}
                  {stat.total != null && (
                    <span className="text-apolo-textTertiary"> / {stat.total}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-b border-apolo-borderSubtle px-5 py-3">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-apolo-textTertiary"
              aria-hidden="true"
            />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por provider, label ou setting key…"
              className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg pl-8 pr-3 py-2 text-xs text-apolo-textPrimary outline-none transition focus:border-apolo-accent"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && !data && (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-apolo-textTertiary">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              Carregando conexões…
            </div>
          )}
          {error && (
            <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-errorBg p-3 text-xs text-apolo-error">
              <AlertCircle size={11} className="mr-1 inline" aria-hidden="true" />
              {error}
            </div>
          )}

          {data && (
            <div className="flex flex-col gap-5">
              {/* Provider LLM keys (main section) */}
              <section>
                <SectionHeader icon={KeyRound} title="Provedores LLM" count={filteredProviders.filter((p) => p.hasValue).length} accent="var(--apolo-accent)" />
                <div className="flex flex-col gap-2">
                  {filteredProviders.length === 0 && (
                    <div className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-4 text-center text-xs text-apolo-textTertiary">
                      Nenhum provider casa com o filtro.
                    </div>
                  )}
                  {filteredProviders.map((p) => (
                    <ConnectionRow
                      key={p.providerKey}
                      providerKey={p.providerKey}
                      label={p.label}
                      Logo={p.Logo}
                      brandColor={p.brandColor}
                      mask={p.mask}
                      hasValue={p.hasValue}
                      docsUrl={p.docsUrl}
                      settingKey={p.settingKey}
                      onSaved={load}
                    />
                  ))}
                </div>
              </section>

              {/* Admin / billing keys */}
              {(adminKeys.length > 0 || data?.adapters?.length > 0) && (
                <section>
                  <SectionHeader icon={Wallet} title="Billing / Admin keys" count={adminKeys.filter((a) => a.has_value).length} accent="var(--apolo-warning)" />
                  <div className="flex flex-col gap-2">
                    {/* Always show OpenAI + Anthropic admin slots since these are the only providers with billing APIs */}
                    {['OPENAI', 'ANTHROPIC'].map((prov) => {
                      const settingKey = `${prov}${ADMIN_KEY_SUFFIX}`;
                      const setting = (data.settings || []).find((s) => s.key === settingKey);
                      const cfg = PROVIDER_CONFIG[prov.toLowerCase()];
                      return (
                        <ConnectionRow
                          key={settingKey}
                          providerKey={prov.toLowerCase()}
                          label={`${cfg.label} · Admin (billing)`}
                          Logo={cfg.Logo}
                          brandColor={cfg.brandColor}
                          mask={setting?.mask}
                          hasValue={!!setting?.has_value}
                          docsUrl={prov === 'OPENAI'
                            ? 'https://platform.openai.com/settings/organization/admin-keys'
                            : 'https://console.anthropic.com/settings/admin-keys'}
                          settingKey={settingKey}
                          onSaved={load}
                        />
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Notificações no celular (ntfy) — agentes + sessões */}
              <NtfySection />

              {/* OAuth connectors (read-only summary) */}
              {connectors.length > 0 && (
                <section>
                  <SectionHeader icon={Link2} title="OAuth conectados" count={connectors.length} accent="var(--apolo-info)" />
                  <ul className="flex flex-col gap-2">
                    {connectors.map((c) => {
                      const Icon = c.kind === 'github' ? Github : Link2;
                      return (
                        <li
                          key={c.id}
                          className="flex items-center gap-3 rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3"
                        >
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-apolo-surface3">
                            <Icon size={14} className="text-apolo-textSecondary" aria-hidden="true" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-apolo-textPrimary">
                              {c.name}
                              {c.status === 'connected' && (
                                <CheckCircle2 size={11} className="text-apolo-success" aria-hidden="true" />
                              )}
                            </div>
                            <div className="text-[11px] text-apolo-textTertiary">
                              {c.kind} · {c.status}
                              {c.last_synced_at && (
                                <> · sync {new Date(c.last_synced_at).toLocaleDateString('pt-BR')}</>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* Budget */}
              <section>
                <SectionHeader icon={Activity} title="Budget mensal" accent="var(--apolo-accent)" />
                <div className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={12} className="text-apolo-accent" aria-hidden="true" />
                      <span className="text-apolo-textSecondary">monthly_budget_usd</span>
                    </div>
                    <code className="text-apolo-textPrimary">
                      {budgetSetting?.value ? `$${budgetSetting.value}` : '—'}
                    </code>
                  </div>
                  <p className="mt-1 text-[10px] text-apolo-textTertiary">
                    CostStatsCard mostra alerta em 80% / 100%. Edite via Settings PUT /api/settings/monthly_budget_usd.
                  </p>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
